import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';

import {
  Container,
  Grid,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const ForgotPassword = () => {
  const { register, handleSubmit, formState: { errors } } = useForm();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const onSubmit = async (data) => {
    setLoading(true);
    setError('');

    // Simulate API call
    setTimeout(() => {
      setLoading(false);
      setSuccess(true);
    }, 1500);
  };

  const imageUrl = "https://horizons-cdn.hostinger.com/f34f6b4a-5701-45b6-9778-48a500b88e4d/00c7de63c68d9d1ea880547b4c0c04a6.png";

  const cleanField = {
    mb: 3,
    '& .MuiOutlinedInput-root': {
      borderRadius: '10px',
      '& fieldset': { borderColor: 'transparent' },
      '&:hover fieldset': { borderColor: 'transparent' },
      '&.Mui-focused fieldset': {
        borderColor: 'transparent',
        boxShadow: '0 0 0 2px rgba(0,0,0,0.15)',
      },
      transition: 'all 0.3s ease',
      background: 'rgba(255,255,255,0.85)',
    },
  };

  return (
    <Container
      maxWidth="lg"
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#ffffff',
        animation: 'fadeIn 0.8s ease',
      }}
    >
      <Grid container spacing={2} sx={{ height: '80vh', alignItems: 'center' }}>
        {/* CENTERED FORM */}
        <Grid item xs={12} md={6} sx={{ display: 'flex', justifyContent: 'center', animation: 'slideUp 0.7s ease' }}>
          <Paper
            elevation={4}
            sx={{
              p: 4,
              width: '100%',
              maxWidth: 450,
              borderRadius: 3,
              background: 'rgba(255,255,255,0.65)',
              backdropFilter: 'blur(12px)',
              boxShadow: '0 8px 28px rgba(0,0,0,0.15)',
            }}
          >
            {/* BACK LINK */}
            <Box sx={{ mb: 3 }}>
              <Link to="/login" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 1 }}>
                <ArrowBackIcon fontSize="small" />
                <Typography variant="body2" color="primary">
                  {/* Back to login */}
                </Typography>
              </Link>
            </Box>

            {/* HEADER */}
            <Box sx={{ mb: 4, textAlign: 'center' }}>
              <img src={imageUrl} alt="RTNZERO Logo" style={{ width: 120, marginBottom: 12 }} />
              <Typography variant="h5" fontWeight="bold" color="black" gutterBottom>
                Reset Password
              </Typography>
              <Typography variant="body2" color="gray">
                Enter your email to receive reset instructions
              </Typography>
            </Box>

            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            {success ? (
              <Alert severity="success" sx={{ mb: 3 }}>
                Password reset instructions have been sent to your email.
              </Alert>
            ) : (
              <form onSubmit={handleSubmit(onSubmit)}>
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  {...register('email', {
                    required: 'Email is required',
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: 'Invalid email address'
                    }
                  })}
                  error={!!errors.email}
                  helperText={errors.email?.message}
                  sx={cleanField}
                />

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  disabled={loading}
                  sx={{
                    mb: 3,
                    py: 1.5,
                    borderRadius: 2,
                    background: 'black',
                    color: 'white',
                    '&:hover': { background: '#333' },
                  }}
                >
                  {loading ? <CircularProgress size={24} /> : 'Send Reset Link'}
                </Button>
              </form>
            )}

            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="body2" color="gray">
                Remember your password?{' '}
                <Link to="/login" style={{ textDecoration: 'none' }}>
                  <Typography component="span" color="black" fontWeight="bold">
                    Sign In
                  </Typography>
                </Link>
              </Typography>
            </Box>
          </Paper>
        </Grid>

        {/* OPTIONAL RIGHT IMAGE */}
        <Grid item xs={12} md={6} sx={{ display: { xs: 'none', md: 'flex' } }}>
          <Box
            sx={{
              width: '100%',
              height: '100%',
              backgroundImage: `url(${imageUrl})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              borderRadius: 3,
              position: 'relative',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(59, 130, 246, 0.1) 100%)',
                borderRadius: 3,
              },
            }}
          >
            <Box sx={{ position: 'absolute', bottom: 40, left: 40, color: 'white' }}>
              <Typography variant="h3" fontWeight="bold" gutterBottom>
                Secure Access
              </Typography>
              <Typography variant="body1">
                Your data security is our top priority
              </Typography>
            </Box>
          </Box>
        </Grid>
      </Grid>

      {/* ANIMATIONS */}
      <style>
        {`
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          @keyframes slideUp {
            from { transform: translateY(25px); opacity: 0; }
            to { transform: translateY(0); opacity: 1; }
          }
        `}
      </style>
    </Container>
  );
};

export default ForgotPassword;
